/* i18n-scan-ignore-file: reproduce el documento del carrier, no es interfaz */
/**
 * Datos de la tarjeta de identificación del miembro.
 *
 * **Su texto no se traduce.** La tarjeta reproduce el documento que emite la
 * aseguradora —el que el asegurado enseña en la consulta—, así que sus rótulos
 * (`Covered` / `Not Covered`, `N/A`, las líneas de reclamaciones con el nombre
 * del proveedor) tienen que coincidir con lo que imprime el carrier. Es
 * contenido, no interfaz: la misma regla que las cabeceras del CSV de
 * importación (plan §10.8).
 */
import {calculateAge, getYearFromEffectiveDate} from '../../../helpers'
import {getMetalLevelValue} from '../../../helpers/consts'
import {NodeMemberPolicy, NodePolicy} from '../../../types/policiesApi'
import {IResultPlanJson, ICostSharing, Plan} from '../../oneLead/oneleadTypes'

export interface PolicyCardData {
  subscriberName: string
  memberName: string
  memberId: string
  policyId: string
  planName: string
  carrierName: string
  effectiveDate: string
  cmsPlanId: string
  planType: string
  metalLevel: string
  medicalDeductible: string
  annualOopm: string
  pcp: string
  specialist: string
  urgentCare: string
  emergencyRoom: string
  genericRx: string
  preferredBrandRx: string
  rxBin: string
  rxPcn: string
  rxGrp: string
  providerPhone: string
  memberServicesPhone: string
  pharmacyPhone: string
  medicalClaimsAddress: string
  website: string
  nurseLine: string
  billingPhone: string
  providerHelpDesk: string
  priorAuthPhone: string
  payerId: string
  providerPortal: string
  pharmacyClaimsAddress: string
  careTeamPhone: string
  mentalHealthPhone: string
  claimsMentalHealth: string
  claimsPharmacy: string
  claimsPediatricVision: string
  claimsMedical: string
  additionalBackLines: string[]
  issuerName: string
  issuerTty: string
  providerNetworkUrl: string
  formularyUrl: string
  benefitsUrl: string
  referralRequired: string
  source: 'LOCAL_ONLY' | 'LOCAL_AND_MARKETPLACE'
}

type CarrierTemplate = 'molina' | 'uhc' | 'ambetter' | 'oscar' | 'sentara' | 'generic'

interface CarrierBackDefaults {
  memberServicesPhone?: string
  providerPhone?: string
  pharmacyPhone?: string
  medicalClaimsAddress?: string
  website?: string
  nurseLine?: string
  billingPhone?: string
  providerHelpDesk?: string
  priorAuthPhone?: string
  payerId?: string
  providerPortal?: string
  pharmacyClaimsAddress?: string
  careTeamPhone?: string
  mentalHealthPhone?: string
  claimsMentalHealth?: string
  claimsPharmacy?: string
  claimsPediatricVision?: string
  claimsMedical?: string
  issuerTty?: string
  additionalBackLines?: string[]
}

const FRONT_BENEFIT_TYPES = {
  pcp: 'PRIMARY_CARE_VISIT_TO_TREAT_AN_INJURY_OR_ILLNESS',
  specialist: 'SPECIALIST_VISIT',
  urgentCare: 'URGENT_CARE_CENTERS_OR_FACILITIES',
  emergencyRoom: 'EMERGENCY_ROOM_SERVICES',
  genericRx: 'GENERIC_DRUGS',
  preferredBrandRx: 'PREFERRED_BRAND_DRUGS',
} as const

const normalizeDateValue = (value?: string | Date | null) => {
  if (!value) return ''
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return ''
    return value.toISOString().split('T')[0]
  }
  const text = `${value}`.trim()
  return text
}

const formatDate = (date?: string | Date | null) => {
  if (!date) return 'N/A'
  const normalized = normalizeDateValue(date)

  // Keep plain YYYY-MM-DD dates timezone-agnostic to avoid off-by-one shifts.
  const yyyyMmDdMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (yyyyMmDdMatch) {
    const [, yyyy, mm, dd] = yyyyMmDdMatch
    return `${mm}/${dd}/${yyyy}`
  }

  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) return 'N/A'
  const mm = String(parsed.getMonth() + 1).padStart(2, '0')
  const dd = String(parsed.getDate()).padStart(2, '0')
  const yyyy = parsed.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A'
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0}).format(
    Number(value)
  )
}

const formatIndFamAmounts = (individual?: number | null, family?: number | null) => {
  if (individual === null || individual === undefined) return 'N/A'
  if (family === null || family === undefined) return formatMoney(individual)
  return `${formatMoney(individual)} / ${formatMoney(family)}`
}

const cleanValue = (value?: string | null) => {
  const text = (value || '').trim()
  return text.length > 0 ? text : 'N/A'
}

const cleanOptionalValue = (value?: string | null) => {
  const text = (value || '').trim()
  return text.length > 0 ? text : ''
}

const resolveWithFallback = (primary?: string | null, fallback?: string | null) => {
  const first = cleanOptionalValue(primary)
  if (first) return first
  const second = cleanOptionalValue(fallback)
  if (second) return second
  return 'N/A'
}

const normalizePlanName = (value?: string | null) => {
  if (!value) return ''
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const tokenizeText = (value?: string | null) => {
  const normalized = normalizePlanName(value)
  if (!normalized) return []
  return normalized.split(' ').filter((token) => token.length > 2)
}

const detectCarrierTemplate = (carrierName?: string | null): CarrierTemplate => {
  const name = (carrierName || '').toLowerCase()
  if (name.includes('molina')) return 'molina'
  if (name.includes('unitedhealthcare') || name.includes('uhc') || name.includes('optum')) return 'uhc'
  if (name.includes('ambetter') || name.includes('superior healthplan')) return 'ambetter'
  if (name.includes('oscar')) return 'oscar'
  if (name.includes('sentara')) return 'sentara'
  return 'generic'
}

const getCarrierBackDefaults = (template: CarrierTemplate): CarrierBackDefaults => {
  switch (template) {
    case 'molina':
      return {
        memberServicesPhone: '(888) 560-5716',
        providerPhone: '(888) 407-6425',
        pharmacyPhone: '(888) 407-6425',
        medicalClaimsAddress: 'Molina Healthcare, PO BOX 22812, Long Beach, CA 90801',
        website: 'MyMolina.com',
        nurseLine: '(888) 275-8750',
        billingPhone: '(800) 375-7421',
        priorAuthPhone: '(855) 322-4076',
        issuerTty: '711',
        additionalBackLines: [
          '24/7 Linea de Consejos de Enfermeras: (866) 648-3537',
          'Inpatient admissions: Provider to notify plan within 24 hours of admission.',
        ],
      }
    case 'uhc':
      return {
        memberServicesPhone: '866-811-2704',
        providerPhone: '888-478-4760',
        pharmacyPhone: '844-569-4143',
        medicalClaimsAddress: 'PO Box 5290, Kingston, NY 12402-5290',
        website: 'myuhc.com/exchange',
        providerPortal: 'UHCprovider.com',
        pharmacyClaimsAddress: 'OptumRx PO Box 650540, Dallas, TX 75265-0540',
      }
    case 'ambetter':
      return {
        memberServicesPhone: '1-877-687-1196',
        pharmacyPhone: '1-877-687-1196',
        providerHelpDesk: '1-844-276-1395',
        medicalClaimsAddress: 'Superior HealthPlan, Attn: CLAIMS, PO Box 5010, Farmington, MO 63640-5010',
        website: 'AmbetterHealth.com',
        nurseLine: '1-877-687-1196',
        payerId: '68069',
        issuerTty: '1-800-735-2989',
      }
    case 'oscar':
      return {
        memberServicesPhone: '855-672-2755',
        providerPhone: '855-672-2755',
        pharmacyPhone: '800-364-6331',
        medicalClaimsAddress: 'Oscar, PO Box 52146, Phoenix, AZ 85072',
        website: 'hioscar.com/member',
        careTeamPhone: '855-672-2755',
        mentalHealthPhone: '855-409-7103',
        payerId: 'OSCAR',
        pharmacyClaimsAddress: 'CVS Caremark',
        claimsMentalHealth: 'Optum',
        claimsPharmacy: 'CVS Caremark',
        claimsPediatricVision: 'Davis Vision',
        claimsMedical: 'Oscar',
        additionalBackLines: ['Pediatric vision claims: Davis Vision', 'Mental health claims: Optum'],
      }
    case 'sentara':
      return {
        memberServicesPhone: '1-866-514-5916',
        providerPhone: '1-800-229-5522',
        providerHelpDesk: '1-800-229-5522',
        nurseLine: '1-800-394-2237',
        pharmacyPhone: '1-800-922-1557',
        mentalHealthPhone: '1-833-717-2310',
        medicalClaimsAddress: 'MEDICAL CLAIMS, PO Box 8203, Kingston, NY 12402-8203',
        website: 'sentarahealthplans.com',
        claimsMentalHealth: 'BEHAVIORAL HEALTH CLAIMS, PO Box 8204, Kingston, NY 12402-8204',
        claimsMedical: 'MEDICAL CLAIMS, PO Box 8203, Kingston, NY 12402-8203',
        issuerTty: '711',
        additionalBackLines: [
          'For emergency care, dial 911 or go to the nearest urgent/emergency facility.',
          'An HMO plan underwritten by Sentara Health Plans',
        ],
      }
    default:
      return {}
  }
}

const getIssuerMatchScore = (candidateIssuer?: string | null, expectedIssuer?: string | null) => {
  const expectedTokens = tokenizeText(expectedIssuer)
  const candidateTokens = tokenizeText(candidateIssuer)
  if (!expectedTokens.length || !candidateTokens.length) return 0
  const candidateSet = new Set(candidateTokens)
  let matches = 0
  expectedTokens.forEach((token) => {
    if (candidateSet.has(token)) matches += 1
  })
  return matches / expectedTokens.length
}

const getPolicyMetalLabel = (policy: NodePolicy) => {
  return getMetalLevelValue(String(policy.metalLevel || '')) || ''
}

const getHolderMember = (policy: NodePolicy) =>
  policy.memberPolicySet?.edges?.find(({node}) => node.rol === 'HOLDER')?.node || null

const getMemberFullName = (member?: NodeMemberPolicy | null) => {
  if (!member) return 'N/A'
  return [member.firstName, member.middleName, member.lastName, member.secondLastName]
    .filter(Boolean)
    .join(' ')
    .trim() || 'N/A'
}

const getInNetworkCostSharing = (costSharings: ICostSharing[] = []) => {
  return costSharings.find((item) => item.network_tier?.toLowerCase().startsWith('in-network')) || null
}

const getBenefitDisplay = (plan: Plan | null | undefined, benefitType: string) => {
  if (!plan?.benefits?.length) return 'N/A'
  const benefit = plan.benefits.find((item) => item.type === benefitType)
  if (!benefit) return 'N/A'
  const inNetwork = getInNetworkCostSharing(benefit.cost_sharings)
  return cleanValue(inNetwork?.display_string || (benefit.covered ? 'Covered' : 'Not Covered'))
}

const getMedicalDeductible = (plan: Plan | null | undefined) => {
  if (!plan) return 'N/A'
  const source = [...(plan.tiered_deductibles || []), ...(plan.deductibles || [])]
  const medicalRows = source.filter((item) =>
    (item.type || '').toLowerCase().includes('medical') ||
    (item.type || '').toLowerCase().includes('combined')
  )
  const individual = medicalRows.find((row) =>
    row.individual === true || (row.family_cost || '').toLowerCase().includes('per person')
  )
  const family = medicalRows.find((row) => row.family === true || (row.family_cost || '').toLowerCase() === 'family')
  return formatIndFamAmounts(individual?.amount, family?.amount)
}

const getMooP = (plan: Plan | null | undefined) => {
  if (!plan) return 'N/A'
  const source = [...(plan.tiered_moops || []), ...(plan.moops || [])]
  const individual = source.find((row) =>
    row.individual === true || (row.family_cost || '').toLowerCase().includes('per person')
  )
  const family = source.find((row) => row.family === true || (row.family_cost || '').toLowerCase() === 'family')
  return formatIndFamAmounts(individual?.amount, family?.amount)
}

const getHouseholdIncome = (policy: NodePolicy) => {
  return (policy.memberPolicySet?.edges || []).reduce((total, {node}) => {
    const income1 = Number(node.annualIncome1 || 0)
    const income2 = Number(node.annualIncome2 || 0)
    const income3 = Number(node.annualIncome3 || 0)
    return total + income1 + income2 + income3
  }, 0)
}

const toMarketplacePerson = (member: NodeMemberPolicy) => ({
  age: Number(calculateAge(member.dateBirth || '') || 0),
  dob: member.dateBirth,
  gender: member.gender || 'Male',
  isPregnant: Boolean(member.isPregnant),
  pregnantWith: 0,
  isParent: false,
  usesTobacco: Boolean(member.usesTobacco),
  hasMec: Boolean(member.hasMec),
  aptcEligible: true,
  utilizationLevel: '',
  relationship: member.relationship || '',
  doesNotCohabitate: false,
  currentEnrollment: null,
})

export const canLookupMarketplaceCardData = (policy?: NodePolicy | null) => {
  if (!policy) return false
  const hasCmsPlan = Boolean((policy.CMSPlanId || '').trim())
  const hasPlanName = Boolean((policy.planName || policy.policyName || '').trim())
  const isFederal = (policy.stateEU?.jurisdictionType || '').toUpperCase() === 'FEDERAL'
  const hasStateCode = Boolean((policy.stateEU?.code || '').trim())
  const hasEffectiveDate = Boolean(normalizeDateValue(policy.effectiveDate))
  return (hasCmsPlan || hasPlanName) && isFederal && hasStateCode && hasEffectiveDate
}

export const buildMarketplaceLookupVariables = (policy: NodePolicy) => {
  if (!canLookupMarketplaceCardData(policy)) return null

  const members = (policy.memberPolicySet?.edges || []).map(({node}) => node)
  const householdIncome = Math.round(getHouseholdIncome(policy))
  const cmsPlanId = (policy.CMSPlanId || '').trim()
  const planName = (policy.planName || policy.policyName || '').trim()
  const metalLabel = getPolicyMetalLabel(policy)

  const base = {
    queryType: 'BUSCAR_PLAN_ID',
    year: getYearFromEffectiveDate(normalizeDateValue(policy.effectiveDate)),
    effectiveDate: normalizeDateValue(policy.effectiveDate),
    householdIncome,
    countyfips: policy.countyFips || '',
    state: policy.stateEU?.code || '',
    zipCode: policy.zipCode || '',
    market: 'Individual',
    numberOfMembers: members.length || 1,
    people: members.map(toMarketplacePerson),
  }

  if (cmsPlanId) {
    return {
      ...base,
      queryType: 'BUSCAR_PLAN_ID',
      planIds: [cmsPlanId],
    }
  }

  return {
    ...base,
    queryType: 'BUSCAR_PLANES',
    planName,
    limit: 25,
    offset: 0,
    order: 'asc',
    metalLevels: metalLabel ? [metalLabel] : null,
  }
}

export const parseMarketplacePlanResponse = (
  payload?: string | null,
  expected?: {planName?: string; issuerName?: string; metalLevel?: string}
) => {
  if (!payload) return {plan: null as Plan | null, errorMessage: null as string | null}
  try {
    const json = JSON.parse(payload) as IResultPlanJson
    if (json.error) return {plan: null, errorMessage: json.error.message || 'Marketplace lookup failed'}
    if (json.validation_errors?.length) {
      return {plan: null, errorMessage: json.validation_errors[0].error || 'Marketplace validation failed'}
    }
    const planFromId = json.plan || null
    if (planFromId) return {plan: planFromId, errorMessage: null}

    const plans = ((json as any)?.plans || []) as Plan[]
    if (!plans.length) return {plan: null, errorMessage: null}

    const targetName = normalizePlanName(expected?.planName)
    const targetIssuer = (expected?.issuerName || '').trim().toLowerCase()
    const targetMetal = (expected?.metalLevel || '').trim().toLowerCase()

    const exactName = targetName
      ? plans.find((item) => normalizePlanName(item.name) === targetName)
      : null
    if (exactName) return {plan: exactName, errorMessage: null}

    const partialByName = targetName
      ? plans.filter((item) => normalizePlanName(item.name).includes(targetName))
      : []
    if (partialByName.length === 1) return {plan: partialByName[0], errorMessage: null}
    if (partialByName.length > 1) {
      const ranked = partialByName.sort((a, b) => {
        const aIssuer = getIssuerMatchScore(a.issuer?.name, expected?.issuerName)
        const bIssuer = getIssuerMatchScore(b.issuer?.name, expected?.issuerName)
        if (aIssuer !== bIssuer) return bIssuer - aIssuer
        const aMetal = (a.metal_level || '').toLowerCase() === targetMetal ? 1 : 0
        const bMetal = (b.metal_level || '').toLowerCase() === targetMetal ? 1 : 0
        return bMetal - aMetal
      })
      return {plan: ranked[0], errorMessage: null}
    }

    const filteredByIssuerMetal = plans.filter((item) => {
      const issuerMatch = targetIssuer ? getIssuerMatchScore(item.issuer?.name, expected?.issuerName) > 0 : true
      const metalMatch = targetMetal ? (item.metal_level || '').toLowerCase() === targetMetal : true
      return issuerMatch && metalMatch
    })
    if (filteredByIssuerMetal.length) return {plan: filteredByIssuerMetal[0], errorMessage: null}

    return {plan: plans[0], errorMessage: null}
  } catch {
    return {plan: null, errorMessage: 'Invalid marketplace response format'}
  }
}

export const buildPolicyCardData = (policy: NodePolicy, plan: Plan | null): PolicyCardData => {
  const holder = getHolderMember(policy)
  const subscriberName = getMemberFullName(holder)
  const memberName = subscriberName
  const issuerAddress = plan?.issuer?.address
    ? [plan.issuer.address.street1, plan.issuer.address.street2, plan.issuer.address.city, plan.issuer.address.state, plan.issuer.address.zipcode]
        .filter(Boolean)
        .join(', ')
    : ''
  const carrierNameRaw = plan?.issuer?.name || policy.carrier?.name || ''
  const carrierTemplate = detectCarrierTemplate(carrierNameRaw)
  const carrierDefaults = getCarrierBackDefaults(carrierTemplate)
  const defaultBackLines = (carrierDefaults.additionalBackLines || []).filter(Boolean)
  const planAny = (plan || {}) as any
  const rxBinFromPlan = cleanOptionalValue(planAny?.rx_bin || planAny?.rxBin)
  const rxPcnFromPlan = cleanOptionalValue(planAny?.rx_pcn || planAny?.rxPcn)
  const rxGrpFromPlan = cleanOptionalValue(planAny?.rx_grp || planAny?.rxGrp)

  return {
    subscriberName,
    memberName,
    memberId: cleanValue(policy.memberId),
    policyId: cleanValue(policy.marketplaceId),
    planName: cleanValue(plan?.name || policy.planName),
    carrierName: cleanValue(carrierNameRaw),
    effectiveDate: formatDate(policy.effectiveDate),
    cmsPlanId: cleanValue(policy.CMSPlanId),
    planType: cleanValue(plan?.type),
    metalLevel: cleanValue(plan?.metal_level || getPolicyMetalLabel(policy)),
    medicalDeductible: getMedicalDeductible(plan),
    annualOopm: getMooP(plan),
    pcp: getBenefitDisplay(plan, FRONT_BENEFIT_TYPES.pcp),
    specialist: getBenefitDisplay(plan, FRONT_BENEFIT_TYPES.specialist),
    urgentCare: getBenefitDisplay(plan, FRONT_BENEFIT_TYPES.urgentCare),
    emergencyRoom: getBenefitDisplay(plan, FRONT_BENEFIT_TYPES.emergencyRoom),
    genericRx: getBenefitDisplay(plan, FRONT_BENEFIT_TYPES.genericRx),
    preferredBrandRx: getBenefitDisplay(plan, FRONT_BENEFIT_TYPES.preferredBrandRx),
    rxBin: resolveWithFallback(rxBinFromPlan, ''),
    rxPcn: resolveWithFallback(rxPcnFromPlan, ''),
    rxGrp: resolveWithFallback(rxGrpFromPlan, ''),
    providerPhone: resolveWithFallback(plan?.issuer?.toll_free, carrierDefaults.providerPhone),
    memberServicesPhone: resolveWithFallback(plan?.issuer?.toll_free, carrierDefaults.memberServicesPhone),
    pharmacyPhone: resolveWithFallback('', carrierDefaults.pharmacyPhone),
    medicalClaimsAddress: resolveWithFallback(issuerAddress, carrierDefaults.medicalClaimsAddress),
    website: resolveWithFallback(plan?.issuer?.individual_url, carrierDefaults.website),
    nurseLine: resolveWithFallback('', carrierDefaults.nurseLine),
    billingPhone: resolveWithFallback('', carrierDefaults.billingPhone),
    providerHelpDesk: resolveWithFallback('', carrierDefaults.providerHelpDesk),
    priorAuthPhone: resolveWithFallback('', carrierDefaults.priorAuthPhone),
    payerId: resolveWithFallback('', carrierDefaults.payerId),
    providerPortal: resolveWithFallback('', carrierDefaults.providerPortal),
    pharmacyClaimsAddress: resolveWithFallback('', carrierDefaults.pharmacyClaimsAddress),
    careTeamPhone: resolveWithFallback('', carrierDefaults.careTeamPhone),
    mentalHealthPhone: resolveWithFallback('', carrierDefaults.mentalHealthPhone),
    claimsMentalHealth: resolveWithFallback('', carrierDefaults.claimsMentalHealth),
    claimsPharmacy: resolveWithFallback('', carrierDefaults.claimsPharmacy),
    claimsPediatricVision: resolveWithFallback('', carrierDefaults.claimsPediatricVision),
    claimsMedical: resolveWithFallback('', carrierDefaults.claimsMedical),
    additionalBackLines: defaultBackLines,
    issuerName: cleanValue(plan?.issuer?.name),
    issuerTty: resolveWithFallback(plan?.issuer?.tty, carrierDefaults.issuerTty),
    providerNetworkUrl: cleanValue(plan?.network_url),
    formularyUrl: cleanValue(plan?.formulary_url),
    benefitsUrl: cleanValue(plan?.benefits_url),
    referralRequired: plan
      ? plan.specialist_referral_required
        ? cleanValue(plan.specialist_referral_text || 'Referral required')
        : 'No referral required'
      : 'N/A',
    source: plan ? 'LOCAL_AND_MARKETPLACE' : 'LOCAL_ONLY',
  }
}
